import { BeneficiariesSummary } from "@beneficiary/domain/models/beneficiaries-summary.model";
import type { BeneficiarySearchFilters } from "@beneficiary/domain/models/beneficiary-search-filters";
import { DeleteBeneficiaryUseCase } from "@beneficiary/domain/usecases/delete-beneficiary.usecase";
import { SearchBeneficiariesByUseCase } from "@beneficiary/domain/usecases/search-beneficiaries-by.usecase";
import { Pagination } from "@core/domain/models/pagination";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { GlobalEntity } from "@entity/domain/models/global-entity.model";
import { GetAllGlobalEntitiesUseCase } from "@entity/domain/usecases/get-all-global-entities.usecase";
import { CreateProceedingMapper } from "@proceeding/data/mappers/create-proceeding.mapper";
import { Proceeding } from "@proceeding/domain/models/proceeding.model";
import { CreateProceedingUseCase } from "@proceeding/domain/usecases/create-proceeding.usecase";
import type { ProceedingFormValuesValidated } from "@proceeding/presentation/components/proceeding-form.component";
import { GlobalProject } from "@project/domain/models/global-project.model";
import { GetAllProjectsUseCase } from "@project/domain/usecases/get-all-projects.usecase";
import { inject, injectable } from "inversify";
import {
    action,
    computed,
    flow,
    flowResult,
    makeObservable,
    observable,
    runInAction,
} from "mobx";

export interface BeneficiaryListTable {
    id: number;
    name: string;
    documentNumber: string;
    projects: string[];
}

interface BeneficiariesListTable {
    beneficiaries: BeneficiaryListTable[];
    count: number;
}

@injectable()
export class BeneficiaryListPageViewModel extends BaseViewModel {
    @observable
    initialLoading: boolean = true;

    @observable
    private _beneficiaries: BeneficiariesSummary = new BeneficiariesSummary(
        [],
        0,
    );

    @observable
    projects: GlobalProject[] = [];

    @observable
    globalEntities: GlobalEntity[] = [];

    @observable
    pagination: Pagination = new Pagination();

    initialFiltersValue: BeneficiarySearchFilters = {
        name: "",
        surname: "",
        documentNumber: "",
        projectIds: [],
        entityIds: [],
    };

    @observable
    filters: BeneficiarySearchFilters = this.initialFiltersValue;

    @computed
    get beneficiariesTable(): BeneficiariesListTable {
        return {
            count: this._beneficiaries.count,
            beneficiaries: this._beneficiaries.beneficiariesSummary.map(
                (beneficiarySummary) => ({
                    id: beneficiarySummary.id,
                    name: `${beneficiarySummary.name} ${beneficiarySummary.surname}`,
                    projects: beneficiarySummary.project ?? [],
                    documentNumber: beneficiarySummary.documentNumber
                        ? beneficiarySummary.documentNumber
                        : "",
                }),
            ),
        };
    }

    @observable
    private _linkActivityModalVisible = false;

    @observable
    private _selectedBeneficiaryId: number | null = null;

    @computed
    get linkActivityModalVisible(): boolean {
        return this._linkActivityModalVisible;
    }

    @action
    setLinkActivityModalVisible(visible: boolean): void {
        this._linkActivityModalVisible = visible;
    }

    @action
    setSelectedBeneficiaryId(id: number | null): void {
        this._selectedBeneficiaryId = id;
    }

    @computed
    get selectedBeneficiaryId(): number | null {
        return this._selectedBeneficiaryId;
    }

    constructor(
        @inject(SearchBeneficiariesByUseCase)
        private readonly searchBeneficiariesByUseCase: SearchBeneficiariesByUseCase,
        @inject(GetAllGlobalEntitiesUseCase)
        private readonly getAllGlobalEntitiesUseCase: GetAllGlobalEntitiesUseCase,
        @inject(GetAllProjectsUseCase)
        private readonly getAllProjectsUseCase: GetAllProjectsUseCase,
        @inject(CreateProceedingUseCase)
        private readonly createProceedingUseCase: CreateProceedingUseCase,
        @inject(CreateProceedingMapper)
        private readonly createProceedingMapper: CreateProceedingMapper,
        @inject(DeleteBeneficiaryUseCase)
        private readonly deleteBeneficiaryUseCase: DeleteBeneficiaryUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await super.didMount();

        this.initViewData();
    }

    @flow
    async *initViewData(): AsyncGenerator<void> {
        yield flowResult(
            Promise.all([
                this.searchBeneficiariesBy(this.filters, this.pagination),
                this.getAllGlobalEntities(),
                this.getAllProjects(),
            ]),
        );

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @flow
    async *createProceeding(
        values: ProceedingFormValuesValidated,
    ): AsyncGenerator<Proceeding | null> {
        try {
            // Si hay un beneficiario seleccionado, añádelo al formulario
            if (this._selectedBeneficiaryId) {
                values.general.beneficiary = this._selectedBeneficiaryId;
            }

            // Transform the form values to a CreateProceeding model
            const createProceedingModel =
                this.createProceedingMapper.mapFromForm(values);

            // Crear el proceeding
            const result = yield this.createProceedingUseCase.execute(
                createProceedingModel,
            );

            // Limpiar estado
            runInAction(() => {
                this._linkActivityModalVisible = false;
                this._selectedBeneficiaryId = null;
            });

            return result;
        } catch (error) {
            console.error("Error al crear la vinculación", error);
            return null;
        }
    }

    @action
    setFilters(filters: BeneficiarySearchFilters): void {
        this.filters = filters;
        this.pagination.reset();

        this.searchBeneficiariesBy(this.filters, this.pagination);
    }

    @action
    setPagination(page: number, pageSize: number): void {
        this.pagination.page = page;
        this.pagination.pageSize = pageSize;

        this.searchBeneficiariesBy(this.filters, this.pagination);
    }

    @flow
    async *searchBeneficiariesBy(
        filters: BeneficiarySearchFilters,
        pagination: Pagination,
    ): AsyncGenerator<void> {
        const beneficiaries = await this.searchBeneficiariesByUseCase.execute(
            pagination,
            filters,
        );

        runInAction(() => {
            this._beneficiaries = beneficiaries;
        });
    }

    @flow
    async *getAllGlobalEntities(): AsyncGenerator<void> {
        const globalEntities = await this.getAllGlobalEntitiesUseCase.execute();

        runInAction(() => {
            this.globalEntities = globalEntities;
        });
    }

    @flow
    async *getAllProjects(): AsyncGenerator<void> {
        const projects = await this.getAllProjectsUseCase.execute();

        runInAction(() => {
            this.projects = projects;
        });
    }

    @flow
    async *deleteBeneficiary(beneficiaryId: number): AsyncGenerator<void> {
        // Execute deletion and refresh list regardless of result (use case handles error toast)
        yield this.deleteBeneficiaryUseCase.execute(beneficiaryId);
        yield flowResult(
            this.searchBeneficiariesBy(this.filters, this.pagination),
        );
    }
}
